import { sendEmailRequest } from '../services/emailDeliveryService';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import {
  createAIPortalRequest,
  getAgentPortalConfig,
  getAIPortalRequest,
  getAIPortalRequests
} from '../services/aiPortalService';
import { decryptPassword } from '../utils/decrypt';
import './AdminDashboard.css';
import './AdminDashboardModern.css';
import './AgentWinds.css';

const POLL_INTERVAL = 5000;
const LONG_RUNNING_MS = 60000;
const TIMEOUT_MS = 300000;
const LOADING_MESSAGE_INTERVAL = 3500;
const EMAIL_API_KEY = 'treas-api-key-2025';
const EMAIL_ENDPOINT = 'https://tophamtechbe.com/api/treas/comm/send-email';
const EMAIL_CONFIG_ENDPOINT = 'https://tophamtechbe.com/api/treas/idx/v1/feature-flag';

// Rotating status lines shown while a request polls. Keyed by request_type so
// the copy matches what the AI worker is actually doing. Both client-lead and
// aw-biz-lead submit request_type 'lead', so one set covers both.
const LOADING_MESSAGES = {
  lead: [
    'Scanning county records for motivated sellers...',
    "Checking foreclosure notices — someone's loss could be your client's gain.",
    'Digging through FSBO listings for unrepresented sellers...',
    'Cross-referencing public notices with property records...',
    'Filtering out leads you have already seen — no repeats, ever.',
    'Searching Facebook groups for buyers venting about the market...',
    "Checking Craigslist for 'must sell' language...",
    'Almost there — ranking the best candidate by intent...'
  ],
  'x-check': [
    'Scanning X for people talking about real estate AI...',
    "Someone's always complaining about their IDX. Let's find them.",
    'Crafting replies under 10 words — brevity is the soul of wit.',
    'Checking for mentions of @Agent_Winds...',
    'Found a few conversations. Deciding which ones are worth a reply...'
  ],
  'x-post': [
    'Deciding what to post today — AI education or a speed flex?',
    "Drafting something that doesn't sound like a bot wrote it...",
    'Choosing the right angle — stop browsing vs. show don\'t sell...',
    'Polishing the tweet. Yes, we draft and redraft. We have standards.'
  ],
  'seo-track': [
    'Googling ourselves so you don\'t have to...',
    "Checking where we rank for 'AI IDX' — brace yourself.",
    'Scanning for competitors who showed up this week...',
    'Finding content gaps we should be blogging about...',
    "Comparing last week's positions to this week's..."
  ],
  'aw-blog': [
    'Picking a keyword that actually has search volume...',
    'Writing something a real person would want to read...',
    'Generating an image that doesn\'t look like AI slop...',
    'Updating the sitemap — Google loves a fresh sitemap.',
    'Triggering an Amplify rebuild. This is the boring part.',
    'Final checks — does this actually answer the question?'
  ],
  cma: [
    'Reviewing the subject property and its immediate market...',
    'Finding recent comparable sales near the property...',
    'Comparing square footage, beds, baths, and sale prices...',
    'Checking active listings competing for the same buyers...',
    'Calculating price-per-square-foot trends...',
    'Building the suggested list-price range...',
    'Preparing the market presentation summary...'
  ],
  default: [
    'The AI is doing its thing. This usually takes 30-60 seconds.',
    'Reading data, making decisions, being useful...',
    'Behind the scenes: search, filter, rank, deliver.',
    'Good things come to those who poll.',
    'Working on it — no lead left behind.'
  ]
};

const formatKey = (key) => key
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (value) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === null || value === undefined || value === '') return 'None';
  return String(value);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return 'Not provided';
  if (typeof value === 'string' && value.trim().startsWith('$')) return value;
  const numericValue = Number(String(value).replace(/[$,]/g, ''));
  if (!Number.isFinite(numericValue)) return String(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(numericValue);
};

const formatWholeNumber = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(numericValue)
    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(numericValue)
    : String(value);
};

const isSafeHttpUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

const escapeEmailHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const emailText = (value) => escapeEmailHtml(formatValue(value));

const buildCmaEmailHtml = (response, recipientName = '') => {
  const data = response?.data || {};
  const subject = data.subject_property || {};
  const range = data.suggested_list_range || {};
  const comps = Array.isArray(data.comps) ? data.comps : [];
  const activeListings = Array.isArray(data.active_listings) ? data.active_listings : [];
  const address = subject.address || response?.market || 'the subject property';
  const brokerageName = SITE_CONFIG.AGENT.BROKERAGE || SITE_CONFIG.AGENT.BROKERAGE_NAME || 'Real Estate Services';
  const brokerageOffice = SITE_CONFIG.OFFICES?.find((office) => office.hq) || SITE_CONFIG.OFFICES?.[0] || {};
  const brokerageEmail = brokerageOffice.email || SITE_CONFIG.AGENT.EMAIL || '';
  const brokeragePhone = brokerageOffice.phone || SITE_CONFIG.AGENT.PHONE || '';
  const logoUrl = SITE_CONFIG.LANDING_PAGE?.AGENT_LOGO ||
    SITE_CONFIG.LANDING_PAGE?.LOGO_IMAGE ||
    SITE_CONFIG.LANDING_PAGE?.BROKERAGE_LOGO ||
    SITE_CONFIG.LANDING_PAGE?.AGENT_IMAGE ||
    SITE_CONFIG.MLS?.LOGO_URL;
  const greeting = recipientName.trim() ? `Hello ${escapeEmailHtml(recipientName.trim())},` : 'Hello,';
  const tableCellStyle = 'padding:10px;border-bottom:1px solid #dbe5e2;vertical-align:top;';
  const compRows = comps.length > 0
    ? comps.map((comp) => `
      <tr>
        <td style="${tableCellStyle}">${emailText(comp.address || 'Address unavailable')}</td>
        <td style="${tableCellStyle}">${emailText(formatCurrency(comp.sold_price))}</td>
        <td style="${tableCellStyle}">${emailText(comp.sold_date)}</td>
        <td style="${tableCellStyle}">${emailText(comp.beds)} / ${emailText(comp.baths)}</td>
        <td style="${tableCellStyle}">${emailText(formatWholeNumber(comp.sqft))}</td>
        <td style="${tableCellStyle}">${emailText(formatCurrency(comp.price_per_sqft))}</td>
        <td style="${tableCellStyle}">${isSafeHttpUrl(comp.source_url) ? `<a href="${escapeEmailHtml(comp.source_url)}" style="color:#2f736b;font-weight:700;">View</a>` : '&mdash;'}</td>
      </tr>`).join('')
    : `<tr><td colspan="7" style="${tableCellStyle}">No sold comparables were returned.</td></tr>`;
  const activeRows = activeListings.length > 0
    ? activeListings.map((listing) => `
      <tr>
        <td style="${tableCellStyle}">${emailText(listing.address || 'Address unavailable')}</td>
        <td style="${tableCellStyle}">${emailText(formatCurrency(listing.list_price))}</td>
        <td style="${tableCellStyle}">${emailText(listing.beds)} / ${emailText(listing.baths)}</td>
        <td style="${tableCellStyle}">${emailText(formatWholeNumber(listing.sqft))}</td>
      </tr>`).join('')
    : `<tr><td colspan="4" style="${tableCellStyle}">No active competing listings were returned.</td></tr>`;

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Comparative Market Analysis</title>
  <style>
    @media only screen and (max-width: 640px) {
      .email-shell { width: 100% !important; }
      .email-pad { padding-left: 18px !important; padding-right: 18px !important; }
      .metric-cell { display: block !important; width: auto !important; border-right: 0 !important; border-bottom: 1px solid #dbe5e2 !important; }
      .scroll-table { display: block !important; overflow-x: auto !important; }
    }
  </style>
</head>
<body style="margin:0;padding:0;background:#eef2f0;color:#1f302e;font-family:Arial,Helvetica,sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:#eef2f0;">
    <tr><td align="center" style="padding:24px 10px;">
      <table role="presentation" class="email-shell" width="680" cellspacing="0" cellpadding="0" border="0" style="width:680px;max-width:100%;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 8px 28px rgba(14,38,35,.12);">
        <tr><td class="email-pad" style="padding:28px 34px;background:#ffffff;border-bottom:4px solid #c99b4a;">
          ${logoUrl ? `<img src="${escapeEmailHtml(logoUrl)}" alt="${escapeEmailHtml(brokerageName)}" style="display:block;max-width:190px;max-height:72px;margin:0 0 18px;">` : ''}
          <div style="color:#d7b77f;font-size:11px;font-weight:700;letter-spacing:1.6px;text-transform:uppercase;">Comparative Market Analysis</div>
          <h1 style="margin:8px 0 0;color:#172724;font-size:25px;line-height:1.3;">${escapeEmailHtml(address)}</h1>
        </td></tr>
        <tr><td class="email-pad" style="padding:30px 34px 16px;">
          <p style="margin:0 0 14px;font-size:16px;line-height:1.6;color:#293c39;">${greeting}</p>
          <p style="margin:0;font-size:15px;line-height:1.65;color:#536764;">I prepared this preliminary market analysis to help you understand the subject property, recent comparable sales, and current competition.</p>
        </td></tr>
        <tr><td class="email-pad" style="padding:10px 34px 16px;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border:1px solid #dbe5e2;border-radius:9px;overflow:hidden;">
            <tr>
              <td class="metric-cell" width="50%" valign="top" style="padding:18px;border-right:1px solid #dbe5e2;">
                <div style="color:#6f827e;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;">Subject property</div>
                <div style="margin-top:7px;color:#172724;font-size:17px;font-weight:700;line-height:1.4;">${escapeEmailHtml(address)}</div>
                <div style="margin-top:9px;color:#536764;font-size:13px;line-height:1.6;">${emailText(subject.beds)} beds &nbsp;&middot;&nbsp; ${emailText(subject.baths)} baths &nbsp;&middot;&nbsp; ${emailText(formatWholeNumber(subject.sqft))} sq. ft.</div>
              </td>
              <td class="metric-cell" width="50%" valign="top" style="padding:18px;">
                <div style="color:#6f827e;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;">Estimated value</div>
                <div style="margin-top:7px;color:#172724;font-size:20px;font-weight:700;line-height:1.35;overflow-wrap:anywhere;">${emailText(formatCurrency(subject.estimated_value || range.mid))}</div>
              </td>
            </tr>
          </table>
        </td></tr>
        <tr><td class="email-pad" style="padding:0 34px 16px;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:#f4f7f6;border-radius:9px;">
            <tr>
              <td class="metric-cell" width="25%" valign="top" style="padding:16px 12px;border-right:1px solid #dbe5e2;"><div style="font-size:17px;font-weight:700;color:#172724;">${emailText(formatCurrency(range.low))}</div><div style="margin-top:5px;font-size:11px;color:#6f827e;">Suggested low</div></td>
              <td class="metric-cell" width="25%" valign="top" style="padding:16px 12px;border-right:1px solid #dbe5e2;"><div style="font-size:17px;font-weight:700;color:#172724;">${emailText(formatCurrency(range.mid))}</div><div style="margin-top:5px;font-size:11px;color:#6f827e;">Suggested midpoint</div></td>
              <td class="metric-cell" width="25%" valign="top" style="padding:16px 12px;border-right:1px solid #dbe5e2;"><div style="font-size:17px;font-weight:700;color:#172724;">${emailText(formatCurrency(range.high))}</div><div style="margin-top:5px;font-size:11px;color:#6f827e;">Suggested high</div></td>
              <td class="metric-cell" width="25%" valign="top" style="padding:16px 12px;"><div style="font-size:13px;font-weight:700;line-height:1.4;color:#172724;overflow-wrap:anywhere;">${emailText(formatCurrency(data.avg_price_per_sqft))}</div><div style="margin-top:5px;font-size:11px;color:#6f827e;">Average price / sq. ft.</div></td>
            </tr>
          </table>
        </td></tr>
        ${data.presentation_summary ? `<tr><td class="email-pad" style="padding:0 34px 20px;"><div style="padding:18px;background:#fbf8f1;border-left:4px solid #c99b4a;border-radius:6px;"><div style="color:#8a6a35;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;">Market summary</div><p style="margin:8px 0 0;color:#435753;font-size:14px;line-height:1.65;">${escapeEmailHtml(data.presentation_summary).replace(/\n/g, '<br>')}</p></div></td></tr>` : ''}
        <tr><td class="email-pad" style="padding:4px 34px 20px;">
          <h2 style="margin:0 0 10px;color:#172724;font-size:18px;">Recent comparable sales</h2>
          <div class="scroll-table">
            <table width="100%" cellspacing="0" cellpadding="0" border="0" style="min-width:610px;border:1px solid #dbe5e2;border-collapse:collapse;font-size:12px;line-height:1.4;">
              <thead><tr style="background:#0b1716;color:#ffffff;"><th align="left" style="padding:10px;">Property</th><th align="left" style="padding:10px;">Sold price</th><th align="left" style="padding:10px;">Date</th><th align="left" style="padding:10px;">Beds/Baths</th><th align="left" style="padding:10px;">Sq. ft.</th><th align="left" style="padding:10px;">$/sq. ft.</th><th align="left" style="padding:10px;">Source</th></tr></thead>
              <tbody>${compRows}</tbody>
            </table>
          </div>
        </td></tr>
        <tr><td class="email-pad" style="padding:0 34px 24px;">
          <h2 style="margin:0 0 10px;color:#172724;font-size:18px;">Active competition</h2>
          <div class="scroll-table">
            <table width="100%" cellspacing="0" cellpadding="0" border="0" style="min-width:480px;border:1px solid #dbe5e2;border-collapse:collapse;font-size:12px;line-height:1.4;">
              <thead><tr style="background:#0b1716;color:#ffffff;"><th align="left" style="padding:10px;">Property</th><th align="left" style="padding:10px;">List price</th><th align="left" style="padding:10px;">Beds/Baths</th><th align="left" style="padding:10px;">Sq. ft.</th></tr></thead>
              <tbody>${activeRows}</tbody>
            </table>
          </div>
        </td></tr>
        <tr><td class="email-pad" style="padding:24px 34px;background:#0b1716;color:#d7e0de;">
          <div style="font-size:16px;font-weight:700;color:#ffffff;">${escapeEmailHtml(brokerageName)}</div>
          ${(brokerageEmail || brokeragePhone) ? `<div style="margin-top:7px;font-size:13px;line-height:1.6;">${brokerageEmail ? `<a href="mailto:${escapeEmailHtml(brokerageEmail)}" style="color:#d7b77f;">${escapeEmailHtml(brokerageEmail)}</a>` : ''}${brokerageEmail && brokeragePhone ? ' &nbsp;&middot;&nbsp; ' : ''}${brokeragePhone ? `<a href="tel:${escapeEmailHtml(brokeragePhone)}" style="color:#d7b77f;">${escapeEmailHtml(brokeragePhone)}</a>` : ''}</div>` : ''}
          <p style="margin:16px 0 0;color:#91a29e;font-size:10px;line-height:1.55;">This preliminary comparative market analysis is based on available public and portal data and is not an appraisal. Pricing and property details should be verified with current MLS data, applicable records, and an in-person property review before making a listing or purchase decision.</p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
};

const formatRequestDate = (value) => {
  if (!value) return 'No date';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value));
};

const getRequestTitle = (request) => {
  const lead = request?.ai_response?.leads?.[0];
  if (lead?.name) return lead.name;
  if (request?.ai_response?.summary) return request.ai_response.summary;
  if (request?.request_detail) return request.request_detail;
  return formatKey(request?.request_type || 'Agent Winds Request');
};

const RecentRequests = ({ requests, loading, error, onSelect }) => (
  <section className="agent-winds-history" aria-labelledby="agent-winds-history-title">
    <div className="agent-winds-history-header">
      <div>
        <p className="agent-winds-kicker">History</p>
        <h2 id="agent-winds-history-title">Recent Requests</h2>
      </div>
      <span>Last 5</span>
    </div>

    {loading && <p className="agent-winds-history-muted">Loading recent requests...</p>}
    {!loading && error && <p className="agent-winds-history-muted">{error}</p>}
    {!loading && !error && requests.length === 0 && (
      <p className="agent-winds-history-muted">No previous requests for this site yet.</p>
    )}

    {!loading && !error && requests.length > 0 && (
      <div className="agent-winds-history-list">
        {requests.map((request) => (
          <button
            type="button"
            className="agent-winds-history-row"
            key={request.id}
            onClick={() => onSelect(request)}
          >
            <span className={`agent-winds-status-dot ${request.status === 'complete' ? 'complete' : 'processing'}`} />
            <span className="agent-winds-history-main">
              <strong>{getRequestTitle(request)}</strong>
              <small>#{request.id} · {formatRequestDate(request.created_at)} · {formatKey(request.request_type || 'request')}</small>
            </span>
            <span className={`agent-winds-history-status ${request.status === 'complete' ? 'complete' : 'processing'}`}>
              {request.status || 'pending'}
            </span>
          </button>
        ))}
      </div>
    )}
  </section>
);

const LeadResults = ({ response }) => {
  const leads = response?.leads || [];

  return (
    <section className="agent-winds-result-panel agent-winds-fade-in" aria-labelledby="agent-winds-results-title">
      <div className="agent-winds-result-header">
        <p className="agent-winds-kicker">Lead Finder</p>
        <h2 id="agent-winds-results-title">Request Complete</h2>
        {response?.summary && <p>{response.summary}</p>}
      </div>

      {leads.length === 0 ? (
        <div className="agent-winds-empty-result">
          <h3>No leads found this time</h3>
          <p>{response?.summary || 'The AI worker completed the search but did not return a lead.'}</p>
        </div>
      ) : (
        <div className="agent-winds-lead-grid">
          {leads.map((lead, index) => (
            <article className="agent-winds-lead-card" key={`${lead.name}-${index}`}>
              <h3>{lead.name || 'Unnamed Lead'}</h3>
              <div className="agent-winds-contact-actions">
                {lead.phone && (
                  <a href={`tel:${lead.phone}`} className="agent-winds-contact-button">
                    {lead.phone}
                  </a>
                )}
                {lead.email && (
                  <a href={`mailto:${lead.email}`} className="agent-winds-contact-button secondary">
                    {lead.email}
                  </a>
                )}
              </div>
              {lead.reason && <p>{lead.reason}</p>}
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

const CMAResults = ({ response }) => {
  const data = response?.data || {};
  const subject = data.subject_property || {};
  const range = data.suggested_list_range || {};
  const comps = Array.isArray(data.comps) ? data.comps : [];
  const activeListings = Array.isArray(data.active_listings) ? data.active_listings : [];
  const estimatedValue = formatCurrency(subject.estimated_value || range.mid);
  const averagePricePerSqft = formatCurrency(data.avg_price_per_sqft);
  const defaultEmailSubject = `Comparative Market Analysis — ${subject.address || response?.market || 'Property'}`;
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [emailSubject, setEmailSubject] = useState(defaultEmailSubject);
  const [emailStatus, setEmailStatus] = useState('idle');
  const [emailError, setEmailError] = useState('');
  const emailPreview = useMemo(
    () => buildCmaEmailHtml(response, recipientName),
    [response, recipientName]
  );

  useEffect(() => {
    setEmailSubject(defaultEmailSubject);
    setEmailStatus('idle');
    setEmailError('');
  }, [defaultEmailSubject]);

  useEffect(() => {
    if (!emailModalOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && emailStatus !== 'sending') setEmailModalOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [emailModalOpen, emailStatus]);

  const openEmailModal = () => {
    setRecipientEmail('');
    setRecipientName('');
    setEmailSubject(defaultEmailSubject);
    setEmailStatus('idle');
    setEmailError('');
    setEmailModalOpen(true);
  };

  const sendCmaEmail = async (event) => {
    event.preventDefault();
    if (!recipientEmail.trim() || !emailSubject.trim() || emailStatus === 'sending') return;

    setEmailStatus('sending');
    setEmailError('');

    try {
      let senderEmail = '';
      let senderPassword = '';
      const configResponse = await fetch(`${EMAIL_CONFIG_ENDPOINT}?siteId=${encodeURIComponent(SITE_CONFIG.SITE_ID)}`, {
        headers: { 'X-API-Key': EMAIL_API_KEY }
      });

      if (configResponse.ok) {
        const config = await configResponse.json();
        senderEmail = config.senderEmail || '';
        if (config.senderEmailPassword) {
          senderPassword = await decryptPassword(config.senderEmailPassword) || '';
        }
      }

      const formData = new FormData();
      formData.append('emailaddress', recipientEmail.trim());
      formData.append('title', emailSubject.trim());
      formData.append('body', emailPreview);
      formData.append('template', 'Comparative Market Analysis');
      if (senderEmail) formData.append('email', senderEmail);
      if (senderPassword) formData.append('emailPassword', senderPassword);

      const sendResponse = await sendEmailRequest(EMAIL_ENDPOINT, {
        method: 'POST',
        headers: { 'X-API-Key': EMAIL_API_KEY },
        body: formData
      });

      if (!sendResponse.ok) {
        let message = 'The CMA email could not be sent. Please try again.';
        try {
          const payload = await sendResponse.json();
          message = payload?.message || payload?.error || message;
        } catch {
          // Keep the friendly fallback when the email service returns no JSON.
        }
        throw new Error(message);
      }

      setEmailStatus('sent');
    } catch (error) {
      setEmailStatus('error');
      setEmailError(error.message || 'The CMA email could not be sent. Please try again.');
    }
  };

  return (
    <section className="agent-winds-result-panel agent-winds-cma agent-winds-fade-in" aria-labelledby="agent-winds-cma-title">
      <div className="agent-winds-result-header">
        <p className="agent-winds-kicker">Comparative Market Analysis</p>
        <h2 id="agent-winds-cma-title">{subject.address || response?.market || 'CMA Complete'}</h2>
        {response?.summary && <p>{response.summary}</p>}
        <button type="button" className="agent-winds-cma-email-button" onClick={openEmailModal}>
          Email CMA
        </button>
      </div>

      <div className="agent-winds-cma-overview">
        <article className="agent-winds-cma-subject">
          <span className="agent-winds-cma-label">Subject property</span>
          <h3>{subject.address || 'Address not provided'}</h3>
          <dl className="agent-winds-cma-property-facts">
            <div><dt>Beds</dt><dd>{formatValue(subject.beds)}</dd></div>
            <div><dt>Baths</dt><dd>{formatValue(subject.baths)}</dd></div>
            <div><dt>Square feet</dt><dd>{formatWholeNumber(subject.sqft)}</dd></div>
          </dl>
        </article>

        <article className="agent-winds-cma-value-card">
          <span className="agent-winds-cma-label">Estimated value</span>
          <strong className={estimatedValue.length > 20 ? 'agent-winds-cma-long-value' : undefined}>
            {estimatedValue}
          </strong>
          <span>Based on the returned comparable-market data</span>
        </article>
      </div>

      <div className="agent-winds-cma-range" aria-label="Suggested list price range">
        <Metric label="Suggested low" value={formatCurrency(range.low)} />
        <Metric label="Suggested midpoint" value={formatCurrency(range.mid)} />
        <Metric label="Suggested high" value={formatCurrency(range.high)} />
        <Metric
          label="Average price / sq. ft."
          value={averagePricePerSqft}
          compact={averagePricePerSqft.length > 20}
        />
      </div>

      {data.presentation_summary && (
        <article className="agent-winds-cma-summary">
          <span className="agent-winds-cma-label">Presentation summary</span>
          <p>{data.presentation_summary}</p>
        </article>
      )}

      <section className="agent-winds-cma-section" aria-labelledby="agent-winds-cma-comps-title">
        <div className="agent-winds-cma-section-heading">
          <h3 id="agent-winds-cma-comps-title">Recent comparable sales</h3>
          <span>{comps.length} {comps.length === 1 ? 'property' : 'properties'}</span>
        </div>
        {comps.length > 0 ? (
          <div className="agent-winds-table-wrap">
            <table className="agent-winds-table agent-winds-cma-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Sold price</th>
                  <th>Sold date</th>
                  <th>Beds / Baths</th>
                  <th>Sq. ft.</th>
                  <th>Price / sq. ft.</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {comps.map((comp, index) => (
                  <tr key={`${comp.address || 'comp'}-${index}`}>
                    <td>{comp.address || 'Address unavailable'}</td>
                    <td>{formatCurrency(comp.sold_price)}</td>
                    <td>{formatValue(comp.sold_date)}</td>
                    <td>{formatValue(comp.beds)} / {formatValue(comp.baths)}</td>
                    <td>{formatWholeNumber(comp.sqft)}</td>
                    <td>{formatCurrency(comp.price_per_sqft)}</td>
                    <td>{isSafeHttpUrl(comp.source_url) ? <a href={comp.source_url} target="_blank" rel="noreferrer">View source</a> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="agent-winds-empty-result"><p>No sold comparables were returned.</p></div>
        )}
      </section>

      <section className="agent-winds-cma-section" aria-labelledby="agent-winds-cma-active-title">
        <div className="agent-winds-cma-section-heading">
          <h3 id="agent-winds-cma-active-title">Active competition</h3>
          <span>{activeListings.length} {activeListings.length === 1 ? 'listing' : 'listings'}</span>
        </div>
        {activeListings.length > 0 ? (
          <div className="agent-winds-table-wrap">
            <table className="agent-winds-table agent-winds-cma-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>List price</th>
                  <th>Beds / Baths</th>
                  <th>Sq. ft.</th>
                </tr>
              </thead>
              <tbody>
                {activeListings.map((listing, index) => (
                  <tr key={`${listing.address || 'listing'}-${index}`}>
                    <td>{listing.address || 'Address unavailable'}</td>
                    <td>{formatCurrency(listing.list_price)}</td>
                    <td>{formatValue(listing.beds)} / {formatValue(listing.baths)}</td>
                    <td>{formatWholeNumber(listing.sqft)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="agent-winds-empty-result"><p>No active competing listings were returned.</p></div>
        )}
      </section>

      {emailModalOpen && (
        <div
          className="agent-winds-email-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && emailStatus !== 'sending') setEmailModalOpen(false);
          }}
        >
          <section
            className="agent-winds-email-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agent-winds-email-title"
            aria-describedby="agent-winds-email-description"
          >
            <div className="agent-winds-email-modal-header">
              <div>
                <p className="agent-winds-kicker">Client Delivery</p>
                <h2 id="agent-winds-email-title">Email this CMA</h2>
                <p id="agent-winds-email-description">Review the recipient, subject, and complete analysis before sending.</p>
              </div>
              <button
                type="button"
                className="agent-winds-email-close"
                aria-label="Close email preview"
                disabled={emailStatus === 'sending'}
                onClick={() => setEmailModalOpen(false)}
              >
                ×
              </button>
            </div>

            {emailStatus === 'sent' ? (
              <div className="agent-winds-email-success" role="status">
                <span aria-hidden="true">✓</span>
                <h3>Email sent</h3>
                <p>The comparative market analysis was sent to {recipientEmail}.</p>
                <button type="button" className="agent-winds-primary-button" onClick={() => setEmailModalOpen(false)}>
                  Done
                </button>
              </div>
            ) : (
              <form className="agent-winds-email-form" onSubmit={sendCmaEmail}>
                <div className="agent-winds-email-fields">
                  <label>
                    Recipient email
                    <input
                      type="email"
                      value={recipientEmail}
                      onChange={(event) => setRecipientEmail(event.target.value)}
                      placeholder="client@example.com"
                      required
                      disabled={emailStatus === 'sending'}
                      autoFocus
                    />
                  </label>
                  <label>
                    Recipient name <span>(optional)</span>
                    <input
                      type="text"
                      value={recipientName}
                      onChange={(event) => setRecipientName(event.target.value)}
                      placeholder="Client name"
                      disabled={emailStatus === 'sending'}
                    />
                  </label>
                  <label className="agent-winds-email-subject-field">
                    Subject
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(event) => setEmailSubject(event.target.value)}
                      required
                      disabled={emailStatus === 'sending'}
                    />
                  </label>
                </div>

                <div className="agent-winds-email-preview-heading">
                  <strong>Email preview</strong>
                  <span>The preliminary-CMA disclaimer is included automatically.</span>
                </div>
                <iframe
                  className="agent-winds-email-preview"
                  title="CMA email preview"
                  srcDoc={emailPreview}
                  sandbox=""
                />

                {emailStatus === 'error' && <p className="agent-winds-email-error" role="alert">{emailError}</p>}

                <div className="agent-winds-email-actions">
                  <button
                    type="button"
                    className="agent-winds-secondary-button"
                    onClick={() => setEmailModalOpen(false)}
                    disabled={emailStatus === 'sending'}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="agent-winds-primary-button" disabled={emailStatus === 'sending'}>
                    {emailStatus === 'sending' ? 'Sending…' : 'Send CMA Email'}
                  </button>
                </div>
              </form>
            )}
          </section>
        </div>
      )}
    </section>
  );
};

const DataResults = ({ response }) => {
  const data = response?.data || {};
  const workflow = response?.workflow;

  if (workflow === 'aw-x-check') {
    return (
      <section className="agent-winds-result-panel agent-winds-fade-in">
        <ResultSummary response={response} />
        <div className="agent-winds-metric-row">
          <Metric label="Tweets Found" value={data.tweets_found} />
          <Metric label="Replies Posted" value={data.replies_posted} />
        </div>
        <div className="agent-winds-list-stack">
          {(data.replies || []).map((reply, index) => (
            <article className="agent-winds-data-card" key={`${reply.tweet_url}-${index}`}>
              <h3>Reply {index + 1}</h3>
              {reply.original_tweet && <p>{reply.original_tweet}</p>}
              {reply.reply_text && <blockquote>{reply.reply_text}</blockquote>}
              {reply.tweet_url && <a href={reply.tweet_url} target="_blank" rel="noreferrer">View Tweet</a>}
            </article>
          ))}
        </div>
      </section>
    );
  }

  if (workflow === 'aw-x-post') {
    return (
      <section className="agent-winds-result-panel agent-winds-fade-in">
        <ResultSummary response={response} />
        <article className="agent-winds-data-card">
          <h3>Published Tweet</h3>
          {data.tweet_text && <p>{data.tweet_text}</p>}
          <div className="agent-winds-badge-row">
            <span>{formatValue(data.content_type)}</span>
            <span>{data.is_thread ? 'Thread' : 'Single Post'}</span>
          </div>
          {data.tweet_url && <a href={data.tweet_url} target="_blank" rel="noreferrer">View Tweet</a>}
        </article>
      </section>
    );
  }

  if (workflow === 'aw-seo-track') {
    return (
      <section className="agent-winds-result-panel agent-winds-fade-in">
        <ResultSummary response={response} />
        <div className="agent-winds-table-wrap">
          <table className="agent-winds-table">
            <thead>
              <tr>
                <th>Keyword</th>
                <th>Position</th>
                <th>Top Competitors</th>
              </tr>
            </thead>
            <tbody>
              {(data.rankings || []).map((ranking, index) => (
                <tr key={`${ranking.keyword}-${index}`}>
                  <td>{ranking.keyword}</td>
                  <td>{ranking.position}</td>
                  <td>{(ranking.top_competitors || []).join(', ') || 'None'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.recommended_blog_topic && (
          <article className="agent-winds-data-card">
            <h3>Recommended Topic</h3>
            <p>{data.recommended_blog_topic}</p>
            <span className="agent-winds-pill">Movement: {formatValue(data.movement_vs_last_week)}</span>
          </article>
        )}
      </section>
    );
  }

  if (workflow === 'aw-blog') {
    return (
      <section className="agent-winds-result-panel agent-winds-fade-in">
        <ResultSummary response={response} />
        <article className="agent-winds-data-card">
          <h3>{data.title || 'Blog Published'}</h3>
          {data.url && <a href={data.url} target="_blank" rel="noreferrer">{data.url}</a>}
          <div className="agent-winds-badge-row">
            <span>Image: {formatValue(data.image_uploaded)}</span>
            <span>Sitemap: {formatValue(data.sitemap_updated)}</span>
            <span>Rebuild: {formatValue(data.amplify_rebuild_triggered)}</span>
          </div>
          {data.keyword_source && <p>Keyword source: {data.keyword_source}</p>}
        </article>
      </section>
    );
  }

  return (
    <section className="agent-winds-result-panel agent-winds-fade-in">
      <ResultSummary response={response} />
      <div className="agent-winds-list-stack">
        {Object.entries(data).map(([key, value]) => (
          <article className="agent-winds-data-card" key={key}>
            <h3>{formatKey(key)}</h3>
            <pre>{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>
          </article>
        ))}
      </div>
    </section>
  );
};

const ResultSummary = ({ response }) => (
  <div className="agent-winds-result-header">
    <p className="agent-winds-kicker">{response?.workflow || 'Agent Winds'}</p>
    <h2>Request Complete</h2>
    {response?.summary && <p>{response.summary}</p>}
  </div>
);

const Metric = ({ label, value, compact = false }) => (
  <div className={`agent-winds-metric${compact ? ' agent-winds-metric--compact' : ''}`}>
    <strong>{formatValue(value)}</strong>
    <span>{label}</span>
  </div>
);

const LoadingState = ({ elapsedMs, requestType }) => {
  const messages = useMemo(
    () => LOADING_MESSAGES[requestType] || LOADING_MESSAGES.default,
    [requestType]
  );
  const [msgIndex, setMsgIndex] = useState(() => Math.floor(Math.random() * messages.length));

  useEffect(() => {
    setMsgIndex(Math.floor(Math.random() * messages.length));
    if (messages.length <= 1) return undefined;

    const interval = setInterval(() => {
      setMsgIndex((prev) => {
        let next = prev;
        while (next === prev) {
          next = Math.floor(Math.random() * messages.length);
        }
        return next;
      });
    }, LOADING_MESSAGE_INTERVAL);

    return () => clearInterval(interval);
  }, [messages]);

  const isLong = elapsedMs > LONG_RUNNING_MS;

  return (
    <section className="agent-winds-loading" role="status" aria-live="polite">
      <div className="agent-winds-spinner" aria-hidden="true" />
      <div>
        <h2>Agent Winds is working</h2>
        <p key={msgIndex} className="agent-winds-loading-message agent-winds-fade-in">
          {messages[msgIndex]}
        </p>
        <span>
          {Math.floor(elapsedMs / 1000)}s elapsed
          {isLong ? ' · still working — big jobs can take up to 30 minutes; you can safely leave this page' : ''}
        </span>
      </div>
      <div className="agent-winds-skeleton-grid" aria-hidden="true">
        <div />
        <div />
        <div />
      </div>
    </section>
  );
};

const AgentWinds = () => {
  const navigate = useNavigate();
  const pollTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const startedAtRef = useRef(null);
  const [portalConfig, setPortalConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [requestDetail, setRequestDetail] = useState('');
  const [requestState, setRequestState] = useState('idle');
  const [activeRequestId, setActiveRequestId] = useState(null);
  const [activeRequestType, setActiveRequestType] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [recentRequests, setRecentRequests] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  // Daily task quota. The limit comes from the agent's global-config entry
  // (daily_task_quota, default 3). Failed runs don't count, so a client whose
  // task errored can try again. Hermes enforces the same limit server-side;
  // this is the friendly front-end gate.
  const [tasksToday, setTasksToday] = useState(0);

  const loadRecentRequests = useCallback(async () => {
    try {
      setHistoryLoading(true);
      setHistoryError('');
      const requests = await getAIPortalRequests(SITE_CONFIG.SITE_ID);
      const today = new Date().toDateString();
      setTasksToday(requests.filter((r) =>
        r.created_at &&
        new Date(r.created_at).toDateString() === today &&
        ['processing', 'complete'].includes(r.status)
      ).length);
      const sortedRequests = requests
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 5);
      setRecentRequests(sortedRequests);
    } catch (err) {
      setHistoryError(err.message || 'Unable to load recent requests.');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      clearTimers();
    };
  }, []);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setConfigLoading(true);
        const config = await getAgentPortalConfig(SITE_CONFIG.SITE_ID);
        setPortalConfig(config);
        setSelectedWorkflowId(config.workflows[0]?.id || '');
      } catch (err) {
        setError(err.message || 'Unable to load Agent Winds configuration.');
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
    loadRecentRequests();
  }, [loadRecentRequests]);

  const selectedWorkflow = useMemo(() => (
    portalConfig?.workflows?.find((workflow) => workflow.id === selectedWorkflowId)
  ), [portalConfig, selectedWorkflowId]);

  const clearTimers = () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    pollTimerRef.current = null;
    elapsedTimerRef.current = null;
  };

  const startElapsedTimer = () => {
    startedAtRef.current = Date.now();
    setElapsedMs(0);
    elapsedTimerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 1000);
  };

  const pollRequest = async (requestId) => {
    try {
      const current = await getAIPortalRequest(requestId, SITE_CONFIG.SITE_ID);
      if (current.status === 'complete') {
        clearTimers();
        setResult(current);
        setRequestState('complete');
        loadRecentRequests();
        return;
      }

      if (Date.now() - startedAtRef.current >= TIMEOUT_MS) {
        clearTimers();
        setRequestState('timeout');
        setError('This is still running in the background — some tasks take up to 30 minutes. You can safely leave this page; the result will appear in Recent Requests when it finishes.');
        return;
      }

      pollTimerRef.current = setTimeout(() => pollRequest(requestId), POLL_INTERVAL);
    } catch (err) {
      clearTimers();
      setRequestState('error');
      setError(err.message || 'Unable to check the request status.');
    }
  };

  const dailyQuota = Number(portalConfig?.agent?.daily_task_quota) >= 1
    ? Number(portalConfig.agent.daily_task_quota)
    : 3;
  const usedToday = tasksToday >= dailyQuota;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedWorkflow || requestState === 'processing' || usedToday) return;

    const normalizedRequestDetail = requestDetail.trim();
    if (selectedWorkflow.id === 'cma' && !normalizedRequestDetail) {
      setError('Enter the complete property address before generating a CMA.');
      setRequestState('error');
      return;
    }

    try {
      clearTimers();
      setError('');
      setResult(null);
      setRequestState('processing');
      setActiveRequestType(selectedWorkflow.request_type);
      startElapsedTimer();

      const created = await createAIPortalRequest({
        siteid: SITE_CONFIG.SITE_ID,
        requester: selectedWorkflow.requester,
        request_type: selectedWorkflow.request_type,
        request_detail: normalizedRequestDetail || selectedWorkflow.id
      });

      setActiveRequestId(created.id);
      loadRecentRequests();
      pollRequest(created.id);
    } catch (err) {
      clearTimers();
      setRequestState('error');
      setError(err.message || 'Unable to create the Agent Winds request.');
    }
  };

  const handleReset = () => {
    clearTimers();
    setRequestState('idle');
    setResult(null);
    setError('');
    setActiveRequestId(null);
    setElapsedMs(0);
    setRequestDetail('');
  };

  const handleCheckStatusLater = () => {
    if (!activeRequestId) return;
    clearTimers();
    setError('');
    setRequestState('processing');
    startElapsedTimer();
    pollRequest(activeRequestId);
  };

  const handleSelectRecentRequest = (request) => {
    clearTimers();
    setError('');
    setActiveRequestId(request.id);
    setActiveRequestType(request.request_type);

    if (request.status === 'complete') {
      setResult(request);
      setRequestState('complete');
      setElapsedMs(0);
      return;
    }

    setResult(null);
    setRequestState('processing');
    startElapsedTimer();
    pollRequest(request.id);
  };

  const renderResult = () => {
    const aiResponse = result?.ai_response;
    if (!aiResponse) return null;
    if (aiResponse.workflow === 'cma' || ['cma', 'comps'].includes(result?.request_type) || ['cma', 'comps'].includes(activeRequestType)) {
      return <CMAResults response={aiResponse} />;
    }
    if (Array.isArray(aiResponse.leads)) return <LeadResults response={aiResponse} />;
    return <DataResults response={aiResponse} />;
  };

  return (
    <div className="admin-dashboard-modern agent-winds-page">
      <header className="admin-header-modern">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title">Agent Winds</h1>
            <p className="dashboard-subtitle">Run AI portal workflows for {SITE_CONFIG.SITE_ID}</p>
          </div>
          <div className="header-right">
            <button onClick={() => navigate('/admin')} className="back-home-btn-modern">
              <span className="btn-icon">←</span>
              Back to Admin
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main-content">
        <div className="agent-winds-shell">
          <section className="agent-winds-panel">
            <div className="agent-winds-panel-header">
              <p className="agent-winds-kicker">AI Portal</p>
              <h2>{portalConfig?.agent?.agent_name || SITE_CONFIG.AGENT.NAME}</h2>
              <p>{portalConfig?.agent?.area || 'Request AI-powered services and review the results from one admin workspace.'}</p>
            </div>

            {configLoading ? (
              <LoadingState elapsedMs={0} />
            ) : !portalConfig?.agent ? (
              <div className="agent-winds-empty-result" role="status">
                <h3>Agent Winds is not enabled for this site</h3>
                <p>No AI Portal enrollment was found for site ID {SITE_CONFIG.SITE_ID}.</p>
              </div>
            ) : (
              <form className="agent-winds-form" onSubmit={handleSubmit}>
                <label htmlFor="agent-winds-workflow">Service</label>
                <select
                  id="agent-winds-workflow"
                  value={selectedWorkflowId}
                  onChange={(event) => {
                    setSelectedWorkflowId(event.target.value);
                    setRequestDetail('');
                    setError('');
                    if (requestState === 'error') setRequestState('idle');
                  }}
                  disabled={requestState === 'processing'}
                >
                  {portalConfig.workflows.map((workflow) => (
                    <option key={workflow.id} value={workflow.id}>
                      {workflow.label}
                    </option>
                  ))}
                </select>

                {selectedWorkflow?.description && (
                  <p className="agent-winds-helper-text">{selectedWorkflow.description}</p>
                )}

                <label htmlFor="agent-winds-detail">
                  {selectedWorkflow?.id === 'cma' ? 'Property Address' : 'Request Detail'}
                </label>
                <input
                  id="agent-winds-detail"
                  value={requestDetail}
                  onChange={(event) => setRequestDetail(event.target.value)}
                  placeholder={selectedWorkflow?.id === 'cma' ? '123 Main Street, City, State ZIP' : 'Optional context for the AI worker'}
                  disabled={requestState === 'processing'}
                  required={selectedWorkflow?.id === 'cma'}
                  autoComplete={selectedWorkflow?.id === 'cma' ? 'street-address' : 'off'}
                />

                {selectedWorkflow?.id === 'cma' && (
                  <p className="agent-winds-helper-text">Include the complete street address, city, state, and ZIP code for the most accurate comparable search.</p>
                )}

                <div className="agent-winds-meta-grid">
                  <span>Site ID: <strong>{SITE_CONFIG.SITE_ID}</strong></span>
                  <span>Requester: <strong>{selectedWorkflow?.requester || 'client'}</strong></span>
                </div>

                {usedToday && requestState !== 'processing' && (
                  <p className="agent-winds-helper-text">
                    You've used {dailyQuota === 1 ? "today's AI task" : `all ${dailyQuota} of today's AI tasks`}. More will be available tomorrow.
                  </p>
                )}

                <button
                  type="submit"
                  className="agent-winds-primary-button"
                  disabled={!selectedWorkflow || requestState === 'processing' || usedToday}
                >
                  {requestState === 'processing'
                    ? 'Processing...'
                    : usedToday
                      ? 'Daily Limit Reached'
                      : selectedWorkflow?.id === 'cma'
                        ? 'Generate CMA'
                        : 'Run Workflow'}
                </button>
              </form>
            )}
          </section>

          <section className="agent-winds-status-panel" aria-live="polite">
            {requestState === 'idle' && (
              <div className="agent-winds-idle">
                <h2>Ready for Request</h2>
                <p>Select an enrolled service and submit a request. Results will appear here when complete.</p>
              </div>
            )}

            {requestState === 'processing' && (
              <>
                <LoadingState elapsedMs={elapsedMs} requestType={activeRequestType} />
                {activeRequestId && <p className="agent-winds-request-id">Request #{activeRequestId}</p>}
              </>
            )}

            {requestState === 'complete' && (
              <>
                {renderResult()}
                <button type="button" className="agent-winds-secondary-button" onClick={handleReset}>
                  Request Another
                </button>
              </>
            )}

            {(requestState === 'error' || requestState === 'timeout') && (
              <div className="agent-winds-error-card" role="alert">
                <h2>{requestState === 'timeout' ? 'Request Still Processing' : 'Request Failed'}</h2>
                <p>{error}</p>
                <div className="agent-winds-action-row">
                  {activeRequestId && (
                    <button type="button" className="agent-winds-secondary-button" onClick={handleCheckStatusLater}>
                      Check Status Later
                    </button>
                  )}
                  <button type="button" className="agent-winds-primary-button" onClick={handleReset}>
                    Retry
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>

        <RecentRequests
          requests={recentRequests}
          loading={historyLoading}
          error={historyError}
          onSelect={handleSelectRecentRequest}
        />
      </main>
    </div>
  );
};

export default AgentWinds;
